import { z } from 'zod/v3';
import {
  ISODate,
  transformNull,
  transformToValidPeriod,
  zIsoDate,
  zValidPeriod,
} from '../../common/utils';
import { zIsoDateTime } from '../../common/utils/schema/zIsoDateTime';
import { ChangedBySchema } from '../../user/schema/ChangedBySchema';
import {
  DataObjectFormType,
  DataObjectOutputType,
  DataObjectType,
  DataSourceFormDataSchema,
  DimensionFormDataSchema,
  VersionDataType,
  VersionQueryDtoType,
} from '../types';
import { AlternativeAlgorithmSetSchema } from './AlternativeAlgorithmFormSchema';
import { RequiredKeyDataSchema } from './RequiredKeyDataSchema';
import {
  ExecutionQueryDtoType,
  AlternativeAlgorithmSetDataType,
  RequiredKeyDataType,
  ValidationRuleFormDataType,
  ValidationRuleQueryDtoType,
} from './types';
import { ExecutionFormDataSchema } from './ExecutionFormDataSchema';
import { ValidationRuleFormDataSchema } from './ValidationRuleFormDataSchema';

export const VersionDataSchema = z.object({
  id: z.number(),
  statusCode: z.string().nullish(),
  versionNumber: z.number().nullable(),
  description: transformNull<string>(z.string(), '').optional(),
  validFrom: zIsoDate().nullish(),
  validTo: zIsoDate().nullish(),
  validPeriod: zValidPeriod().optional(),
  createdBy: ChangedBySchema.nullable(),
});

export const IndicatorFormDataSchema = z.object({
  id: z.number().nullish(),
  code: transformNull<string>(z.string(), ''),
  name: transformNull<string>(z.string(), ''),
  description: transformNull<string | undefined>(z.string(), '').nullish(),
  decimals: transformNull<number | undefined>(z.number(), undefined).optional(),
  sequenceNumber: transformNull<number | undefined>(
    z.number(),
    undefined,
  ).optional(),
  periodTypeId: transformNull<number | undefined>(
    z.number(),
    undefined,
  ).optional(),
  multiplierId: transformNull<number | undefined>(
    z.number(),
    undefined,
  ).optional(),
  width: z.number(),
  unitId: transformNull<number | undefined>(z.number(), undefined).optional(),
  intermediate: transformNull(z.boolean(), false).optional(),
  imputeFromIndicator: transformNull<string | undefined>(
    z.string(),
    '',
  ).nullish(),
  required: transformNull(z.boolean(), false).optional(),
});

export const defaultExecution = (custom?: any) => ({
  id: null,
  dataObjectGroupId: null,
  dataObjectDescriptionId: null,
  autoLoadingPaused: false,
  loadSample: true,
  sampleLoadingDays: 0,
  sampleLoadingTime: '00:00',
  executionMethodId: null,
  executionTime: null,
  periodLoadingTypeId: null,
  periodCount: null,
  periodSkipCount: null,
  executionCalendar: [],
  executionPeriods: [],
  logLoadingSuccess: false,
  publicationTime: null,
  executionPublications: [],
  sourceDataVersionId: null,
  ...custom,
});

export const DataObjectFormDataSchema = transformToValidPeriod(
  z.object({
    dataObjectType: z.nativeEnum(DataObjectType),
    outputTypeCode: z.nativeEnum(DataObjectOutputType).nullish(),
    outputDataObjectId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    outputDataObjectCode: transformNull<string>(z.string(), '').optional(),
    outputDataObjectName: transformNull<string>(z.string(), '').optional(),
    versionId: transformNull(z.number().readonly(), -1),
    dataObjectId: z.number().nullish(),
    statusCode: transformNull<string>(z.string(), ''),
    versionNumber: transformNull<string>(z.string(), '').optional(),
    versionDescription: transformNull<string>(z.string(), '').optional(),
    validFrom: transformNull<ISODate>(zIsoDate(), '' as ISODate),
    validTo: zIsoDate().nullable(),
    code: transformNull<string>(z.string(), '').optional(),
    name: transformNull<string>(z.string(), '').optional(),
    description: transformNull<string>(z.string(), '').optional(),
    indicatorStatus: transformNull<boolean>(z.boolean(), false).optional(),
    aggregateValues: transformNull<boolean>(z.boolean(), false).optional(),
    hasActiveSubmittedDataVersions: z.boolean().optional(),
    hasRealData: z.boolean().optional(),
    usedInAlgorithmSource: z.boolean().optional(),
    groupId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    subgroupId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    groupSubgroupName: transformNull<string | undefined>(
      z.string(),
      undefined,
    ).optional(),
    confidentialityStatusId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    domainId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    dataProviderGroupId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    frequencyId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    externalCode: transformNull<string>(z.string(), '').optional(),
    responsibleId: transformNull<number | undefined>(z.number(), undefined),
    responsibleUsername: transformNull<string>(z.string(), '').optional(),
    confidential: transformNull(z.boolean(), false),
    accessGroupId: transformNull<number | undefined>(
      z.number(),
      undefined,
    ).optional(),
    accessGroupName: transformNull<string | undefined>(
      z.string(),
      undefined,
    ).optional(),
    subjectDimension: transformNull(z.boolean(), true).optional(),
    sourceIds: transformNull<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ).optional(),
    recipientIds: transformNull<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ).optional(),
    allVersions: transformNull<VersionDataType[], VersionQueryDtoType[]>(
      z.array(VersionDataSchema),
      [],
    ),
    dimensions: z.array(DimensionFormDataSchema).optional(),
    indicators: z.array(IndicatorFormDataSchema).optional(),
    dataSources: z.array(DataSourceFormDataSchema).optional(),
    algorithmsCount: transformNull<number>(z.number(), 0),
    execution: transformNull<
      z.infer<typeof ExecutionFormDataSchema>,
      ExecutionQueryDtoType
    >(ExecutionFormDataSchema, defaultExecution({ executionDeadlines: [] })),
    validationRules: transformNull<
      ValidationRuleFormDataType[],
      ValidationRuleQueryDtoType[]
    >(z.array(ValidationRuleFormDataSchema), []),
    requiredKeys: transformNull<RequiredKeyDataType[]>(
      z.array(RequiredKeyDataSchema),
      [],
    ),
    alternativeAlgorithmSets: transformNull<AlternativeAlgorithmSetDataType[]>(
      z.array(AlternativeAlgorithmSetSchema),
      [],
    ),
    modifiedAt: zIsoDateTime().nullable(),
    modifiedBy: ChangedBySchema.nullable(),
  }),
)
  .transform(({ groupId, subgroupId, ...rest }) => ({
    ...rest,
    groupAndSubgroupId: groupId
      ? subgroupId
        ? groupId + '-' + subgroupId
        : String(groupId)
      : undefined,
  }))
  .transform((data) => ({
    ...data,
    formType:
      data.dataObjectType === DataObjectType.IN
        ? DataObjectFormType.IN
        : data.outputTypeCode === DataObjectOutputType.INTERMEDIATE
          ? DataObjectFormType.INTERMEDIATE
          : data.outputTypeCode === DataObjectOutputType.REPORT
            ? DataObjectFormType.REPORT
            : DataObjectFormType.OUTPUT,
  }))
  .transform(
    ({ execution: { executionDeadlines, ...execution }, ...data }) => ({
      ...data,
      execution,
      executionDeadlines,
    }),
  )
  // Workaround for known issue: https://github.com/colinhacks/zod/issues/2732
  .transform(
    (data) =>
      data as Omit<typeof data, 'versionId'> & { readonly versionId: number },
  );
